import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, LinearProgress } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import useImage from 'use-image'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import Select from '@/components/Select'
import { useAppSelector } from '@/hooks/redux'
import { setImagePixelInfo } from '@/store/appSlice'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'
import { GREYCARD, GREY_CARD_OPTIONS } from '@/utils/constants'

import { getAverageColor } from './konva/utils'

interface CalibrationDialogProps {
  isOpen: boolean
  onClose: () => void
  onCalibrate: () => void
  onGreyCardChange: (value: string) => void
  disabled?: boolean
  imageId?: string
}

const CalibrationDialog: React.FC<CalibrationDialogProps> = ({
  isOpen,
  onClose,
  onCalibrate,
  onGreyCardChange,
  disabled,
  imageId,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calibration',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'buttons',
  })

  const [step, setStep] = useState(1)
  const dispatch = useDispatch()

  const canvasImage = useAppSelector((state) => state.app.canvasImage)
  const newImages = useAppSelector((state) => state.app.newImages)
  const greyCardValue = useAppSelector((state) => state.app.greyCardValue)

  const selectedImage = imageId
    ? newImages.find((img) => img.id === imageId) || canvasImage
    : canvasImage || newImages[0]

  const [image] = useImage(selectedImage?.image, 'anonymous')

  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  const currentImageId = selectedImage?.id
  const preselectedGreyCard = currentImageId
    ? greyCardValue[currentImageId]
    : ''

  useEffect(() => {
    if (isOpen) {
      setStep(1)
    }
  }, [isOpen])

  useEffect(() => {
    if (!image || !canvasRef.current || step !== 2) return
    if (!image.complete) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const scaleX = canvas.width / image.width
    const scaleY = canvas.height / image.height
    const scale = Math.min(scaleX, scaleY)

    const drawWidth = image.width * scale
    const drawHeight = image.height * scale
    const offsetX = (canvas.width - drawWidth) / 2
    const offsetY = (canvas.height - drawHeight) / 2

    ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight)
  }, [image, step])

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!image || !canvasRef.current) return

    const rect = canvasRef.current.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const clickY = e.clientY - rect.top

    const scaleX = canvasRef.current.width / image.width
    const scaleY = canvasRef.current.height / image.height
    const scale = Math.min(scaleX, scaleY)

    const drawWidth = image.width * scale
    const drawHeight = image.height * scale
    const offsetX = (canvasRef.current.width - drawWidth) / 2
    const offsetY = (canvasRef.current.height - drawHeight) / 2

    const imageX = (clickX - offsetX) / scale
    const imageY = (clickY - offsetY) / scale

    const rawWidth = selectedImage?.raw_width
    const rawHeight = selectedImage?.raw_height

    const mappedX = (rawWidth / image.width) * imageX
    const mappedY = (rawHeight / image.height) * imageY

    const hiddenCanvas = document.createElement('canvas')
    hiddenCanvas.width = image.width
    hiddenCanvas.height = image.height
    const ctx = hiddenCanvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(image, 0, 0, image.width, image.height)

    const { labColor } = getAverageColor(imageX, imageY, 1, 1, {
      current: hiddenCanvas,
    })

    dispatch(
      setImagePixelInfo({
        x: mappedX,
        y: mappedY,
        labColor,
      }),
    )
    onCalibrate?.()

    if (currentImageId) {
      dispatch(
        setImageCalibrationStatus({
          id: currentImageId,
          status: ImageCalibrationStatus.CALIBRATED,
        }),
      )
    }
  }

  return (
    <Dialog
      triggerElement={null}
      isOpen={isOpen}
      onClose={onClose}
      title={t('title')}
      maxWidth="xl"
      paperStyles={{ width: '800px' }}
      progressBar={
        <LinearProgress
          variant="determinate"
          value={(step / 2) * 100}
          sx={{
            height: 8,
            backgroundColor: '#4A6880',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#8EC9F6',
            },
          }}
        />
      }
      content={
        <>
          {step === 1 && (
            <Box>
              <Box fontWeight={500} fontSize="16px" mb={2}>
                1. {t('selectValue')}
              </Box>
              <Select
                options={GREY_CARD_OPTIONS}
                name={GREYCARD}
                onFormValueChange={(_, v) => onGreyCardChange(v)}
                value={
                  preselectedGreyCard ||
                  selectedImage?.calibrate_meta?.protocol ||
                  (selectedImage?.calibrated_image ? '79' : '')
                }
                renderValue={(selected: any) =>
                  selected ? (
                    `L*${selected}`
                  ) : (
                    <span style={{ color: '#999999' }}>{t('select')}</span>
                  )
                }
              />
            </Box>
          )}
          {step === 2 && (
            <>
              <Box fontWeight={500} fontSize="16px" mb={2}>
                2. {t('clickOnCircle')}
              </Box>

              {image && (
                <canvas
                  ref={canvasRef}
                  width={754}
                  height={500}
                  style={{ cursor: 'crosshair', maxWidth: '100%' }}
                  onClick={handleCanvasClick}
                />
              )}
            </>
          )}
        </>
      }
      actionButtons={
        <>
          {step === 1 && (
            <>
              <Button
                variant="outlined"
                label={translate('cancel')}
                onClick={onClose}
              />
              <Button
                variant="contained"
                label={translate('next')}
                onClick={() => setStep(2)}
                disabled={disabled}
                endIcon={<ChevronRightIcon />}
              />
            </>
          )}
          {step === 2 && (
            <Button
              variant="outlined"
              label={translate('back')}
              onClick={() => setStep(1)}
              startIcon={<ChevronLeftIcon />}
            />
          )}
        </>
      }
    />
  )
}

export default CalibrationDialog
